import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, X, Users, Merge } from "lucide-react";

let cache = null;
const listeners = new Set();
const setCache = (d) => { cache = d; listeners.forEach((l) => l(d)); };
export const reloadEmployees = () => api.get("/admin/employees").then((r) => setCache(r.data)).catch(() => {});

export const useEmployees = () => {
  const [d, setD] = useState(cache);
  useEffect(() => { listeners.add(setD); if (!cache) reloadEmployees(); return () => listeners.delete(setD); }, []);
  return d || { names: [], unknown: [] };
};

// Picker for the operator: employees from the list; a name not in the list is kept and flagged
export const OperatorSelect = ({ value, onChange, className = "", testid }) => {
  const { names } = useEmployees();
  if (!names.length) return <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Nome operatore" className={className} data-testid={testid} />;
  const known = names.some((n) => n.toLowerCase() === (value || "").trim().toLowerCase());
  return (
    <select value={known ? names.find((n) => n.toLowerCase() === value.trim().toLowerCase()) : value || ""} onChange={(e) => onChange(e.target.value)}
      className={`${className} ${value && !known ? "text-[#E63946] font-bold" : ""}`} data-testid={testid}>
      {!value && <option value="">Scegli operatore…</option>}
      {value && !known && <option value={value}>{value} (non in elenco)</option>}
      {names.map((n) => <option key={n} value={n}>{n}</option>)}
    </select>
  );
};

export const EmployeesManager = () => {
  const { names, unknown } = useEmployees();
  const [add, setAdd] = useState("");
  const [target, setTarget] = useState({});
  const save = async (list) => {
    try { setCache((await api.put("/admin/employees", { names: list })).data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const merge = async (u) => {
    const to = target[u.name] || u.suggest || u.name;
    try {
      const { data } = await api.post("/admin/employees/merge", { from_name: u.name, to_name: to });
      toast.success(`${data.updated} ${data.updated === 1 ? "scheda ore ora" : "schede ore ora"} a nome ${to}`); reloadEmployees();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const sel = "h-10 rounded-md bg-[#12141A] border border-white/10 px-2 text-sm";

  return (
    <div className="space-y-5" data-testid="employees-manager">
      <div>
        <div className="flex flex-wrap gap-2 mb-3">
          {names.length === 0 && <p className="text-sm text-muted-foreground">Nessun dipendente: aggiungi i nomi qui sotto.</p>}
          {names.map((n, i) => (
            <span key={n} className="inline-flex items-center gap-1 rounded-full bg-white/10 pl-3 pr-1 py-1 text-sm" data-testid={`employee-${i}`}>
              {n}<button onClick={() => save(names.filter((x) => x !== n))} className="p-1 rounded-full hover:bg-white/10" aria-label={`Togli ${n}`} data-testid={`employee-${i}-remove`}><X className="w-3.5 h-3.5" /></button>
            </span>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); if (add.trim()) { save([...names, add.trim()]); setAdd(""); } }} className="flex gap-2">
          <Input value={add} onChange={(e) => setAdd(e.target.value)} placeholder="Nome dipendente (es. Matteo)" className="bg-[#12141A] border-white/10" data-testid="employee-add-input" />
          <Button type="submit" className="rounded-full bg-[#E63946] hover:bg-[#D62828] text-white" data-testid="employee-add-btn"><Plus className="w-4 h-4 mr-1" />Aggiungi</Button>
        </form>
      </div>
      {unknown.length > 0 && (
        <div data-testid="employees-unknown">
          <div className="text-xs uppercase text-muted-foreground mb-2 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />Nomi nelle ore da sistemare</div>
          <div className="space-y-2">
            {unknown.map((u, i) => (
              <div key={u.name} className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_160px_auto] gap-2 items-center rounded-lg bg-[#12141A] border border-white/10 p-2" data-testid={`unknown-${i}`}>
                <div className="text-sm"><b className="text-[#FF9E00]">{u.name}</b> <span className="text-xs text-muted-foreground">· {u.sheets} {u.sheets === 1 ? "scheda" : "schede"} · {u.hours.toLocaleString("it-IT")} h</span></div>
                <select value={target[u.name] || (names.includes(u.suggest) ? u.suggest : "__new")} onChange={(e) => setTarget({ ...target, [u.name]: e.target.value === "__new" ? u.name : e.target.value })} className={`${sel} col-span-2 sm:col-span-1 order-3 sm:order-none`} data-testid={`unknown-${i}-target`}>
                  {names.map((n) => <option key={n} value={n}>diventa {n}</option>)}
                  <option value="__new">aggiungi come nuovo</option>
                </select>
                <Button size="sm" onClick={() => merge(u)} className="rounded-full bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid={`unknown-${i}-merge`}><Merge className="w-4 h-4 mr-1" />Sistema</Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
